// The table as one seat sees it, built only from the server's messages (design 4 P3: the client needs no
// rules code to play). `apply` is a pure reducer: (view, ServerMessage, now) -> view. Resync rebuilds
// the current hand from its full event list, so a reconnect lands on the same view as staying connected.

import type { Event, GameEnd, HandEnd, HandStart, Win, ExhaustiveDraw, AbortiveDraw } from "../pb/mj/record/v1/record_pb";
import { ActionType, HandEnd_Outcome } from "../pb/mj/record/v1/record_pb";
import type { Prompt, ServerMessage, TableAssigned } from "../pb/mj/protocol/v1/protocol_pb";
import { AckStatus, ErrorCode } from "../pb/mj/protocol/v1/protocol_pb";
import { Preset } from "../pb/mj/riichi/v1/riichi_pb";
import { NO_TILE, isTile, kindOf, sortTiles } from "./tiles";

export const SEATS = 4;
const LIVE_WALL = 70; // 136 tiles − 14 in the dead wall − 52 dealt

// Every tile is one physical tile for the whole hand (design 10 §3), known by its index in the hand's wall
// permutation (design 2 §3.4), which every seat can follow even while the tile's face is hidden: the deal
// takes 0–51 in a fixed order, live draws take 52 upward, replacements and indicators carry their index.
export const FIRST_LIVE = 52;
const FIRST_REPLACEMENT = 122;
const FIRST_INDICATOR = 126;

/** The wall indices a seat is dealt, in dealing order: two stacks a turn three times from the dealer, then
 *  one tile each (design 2 §3.4). `fromDealer` is the seat's place after the dealer (0 for the dealer). */
export function dealWalls(fromDealer: number): number[] {
  const out: number[] = [];
  for (let block = 0; block < 3; block++) for (let j = 0; j < 4; j++) out.push(16 * block + 4 * fromDealer + j);
  out.push(48 + fromDealer);
  return out;
}

export interface RiverTile {
  tile: number;
  tsumogiri: boolean;
  riichi: boolean; // the declaration tile, drawn sideways
  calledAway: boolean;
  byServer?: boolean; // the server played it for a seat out of time or away
}

export interface Meld {
  type: ActionType; // CHI, PON, OPEN_KAN, ADDED_KAN, CLOSED_KAN
  tiles: number[];
  called: number; // NO_TILE for a closed kan
  from: number; // the seat the called tile came from; the owner for a closed kan
}

// A penalty's record (mjcore/include/mj/event.h): a Payment of no points with this bit in its reason.
const PENALTY_REASON = 0x8000;
export const PENALTY_DEAD_HAND = 2;
export const PENALTY_FINE = 3;
export const PENALTY_CHONBO = 4;
const PENALTY_CALLOUTS: Record<number, string> = { 1: "WARNING", 2: "DEAD_HAND", 3: "FINE", 4: "CHONBO" };

export interface SeatView {
  known: boolean; // its tiles are visible to this view: the own seat in play, every seat in a replay
  concealed: number[]; // known seats: tile ids in hand order; others: empty
  drawn: number; // known seats: the tile just drawn, shown apart; NO_TILE otherwise
  hidden: number; // other seats: how many concealed tiles they hold (counting a drawn one)
  apart: boolean; // other seats: the last of those is a drawn tile, held apart until the seat discards
  slots: SlotsSeen | null; // where the seat's last discard left its hand, until it next draws or calls
  slotWalls: number[]; // hidden seats: the wall index of the tile in each slot, left to right
  drawnWall: number; // hidden seats: the wall index of the drawn tile held apart, -1 when none
  shown: number[] | null; // hidden seats: the hand laid face up at the hand's end (a win, a tenpai at a draw)
  winning: number; // the winning tile, when this seat won (NO_TILE otherwise)
  river: RiverTile[];
  melds: Meld[];
  riichi: boolean;
  points: number;
  dead: boolean; // a dead hand this hand (design 14 §6.6)
}

// What every seat sees of a discard's hand slots (design 8 §2): the slot it left, counted left to right
// with a drawn tile apart at the right, and, when another tile than the drawn one went, where that went.
export interface SlotsSeen {
  from: number;
  keptDraw: boolean;
  to: number;
  seq: number; // the discard's event: each discard's animation plays once
}

export interface Player {
  id: string;
  name: string;
  bot: boolean;
  drawnOnTop: boolean; // lays a drawn tile sideways on top of the hand (design 10 T6)
}

export interface HandInfo {
  index: number;
  dealer: number;
  round: number; // 0 East, 1 South, 2 West
  handInRound: number;
  honba: number;
  sticks: number;
  indicators: number[];
  ura: number[]; // ura dora indicators turned over by a riichi win
  wallLeft: number;
  nextLive: number; // the wall index the next live draw takes
  replacements: number; // replacement draws so far
}

export interface HandResult {
  end: HandEnd | null; // null while the hand's closing events are in but its HandEndView is not
  wins: Win[];
  exhaustive?: ExhaustiveDraw;
  abortive?: AbortiveDraw;
  nagashi?: number[]; // the seats paid a nagashi mangan at an exhaustive draw
  pointsBefore: number[];
  honba: number; // the hand's counters as it ended, before the next hand's are set
  sticks: number; // riichi deposits on the table, this hand's included
}

export interface OpenPrompt {
  prompt: Prompt;
  receivedAt: number; // ms, the client's clock
  sent: boolean; // an Act is on its way: the controls lock
  rejected: boolean; // the server answered ILLEGAL
}

export interface TurnClock {
  seat: number;
  baseMsLeft: number;
  bankMsLeft: number;
  receivedAt: number;
}

export interface View {
  gameId: string;
  seat: number;
  players: Player[];
  redFives: boolean;
  untimed: boolean; // the table runs without clocks: no countdowns
  preset: Preset;
  phase: "waiting" | "idle" | "shuffling" | "playing" | "ended"; // idle: the server says no game is running
  hand: HandInfo | null;
  seats: SeatView[];
  lastSeq: number;
  lastDiscard: { seat: number; index: number } | null;
  prompt: OpenPrompt | null;
  clock: TurnClock | null;
  connections: { connected: boolean; auto: boolean }[];
  result: HandResult | null; // the last hand's end, until the next hand starts
  gameEnd: GameEnd | null;
  error: { code: ErrorCode; detail: string } | null; // the server's last Error message
  aids: { waits: number[]; furiten: boolean; noYaku: number[]; noRon: number[] } | null; // unranked: this seat's waits (kinds) and furiten, and which of them would not win it
  // The last call, riichi or win, announced briefly on that seat's side of the table (design 8 item 5): the
  // seat, the action's name (an ActionType key of strings' actions) and the event, so each plays once.
  callout: { seat: number; action: string; seq: number } | null;
  penalties: { seat: number; kind: number }[]; // this game's so far: what a table without assists ruled (design 14 §6.6)
  forYou: number[]; // the tiles the server played for this seat that it has not yet been told of: until its next press, or the next hand
  wallOf: Record<number, number>; // every tile whose face this view knows: tile id -> wall index
}

function emptySeat(points = 0): SeatView {
  return { known: false, dead: false, concealed: [], drawn: NO_TILE, hidden: 0, apart: false, slots: null, slotWalls: [], drawnWall: -1, shown: null, winning: NO_TILE, river: [], melds: [], riichi: false, points };
}

export function emptyView(): View {
  return {
    gameId: "",
    seat: 0,
    players: [],
    redFives: false,
    untimed: false,
    preset: Preset.PRESET_UNSPECIFIED,
    phase: "waiting",
    hand: null,
    seats: Array.from({ length: SEATS }, () => emptySeat()),
    lastSeq: -1,
    lastDiscard: null,
    prompt: null,
    clock: null,
    connections: Array.from({ length: SEATS }, () => ({ connected: true, auto: false })),
    result: null,
    gameEnd: null,
    error: null,
    aids: null,
    callout: null,
    penalties: [],
    forYou: [],
    wallOf: {},
  };
}

export function clone(v: View): View {
  return {
    ...v,
    hand: v.hand && { ...v.hand, indicators: [...v.hand.indicators], ura: [...v.hand.ura] },
    seats: v.seats.map((s) => ({ ...s, concealed: [...s.concealed], slotWalls: [...s.slotWalls], river: s.river.map((r) => ({ ...r })), melds: s.melds.map((m) => ({ ...m, tiles: [...m.tiles] })) })),
    connections: v.connections.map((c) => ({ ...c })),
    forYou: [...v.forYou],
    result: v.result && { ...v.result, wins: [...v.result.wins] },
    wallOf: { ...v.wallOf },
  };
}

function assigned(v: View, t: TableAssigned): void {
  if (v.gameId !== t.gameId) {
    const fresh = emptyView();
    Object.assign(v, fresh);
  }
  v.gameId = t.gameId;
  v.seat = t.seat;
  v.players = t.players.map((p, i) => ({ id: p.playerId, name: p.displayName, bot: p.bot, drawnOnTop: t.drawnOnTop[i] ?? false }));
  v.redFives = t.rules?.preset === Preset.TENHOU_4P_RED;
  v.preset = t.rules?.preset ?? Preset.PRESET_UNSPECIFIED;
  v.untimed = t.table?.clocks !== undefined && t.table.clocks.baseMs === 0;   // a table without clocks
}

function handStart(v: View, start: HandStart, handIndex: number): void {
  v.hand = {
    index: handIndex,
    dealer: start.dealer,
    round: start.round,
    handInRound: start.handInRound,
    honba: start.repeatCount,
    sticks: start.sticks,
    indicators: start.initialIndicator !== NO_TILE ? [start.initialIndicator] : [],
    ura: [],
    wallLeft: LIVE_WALL,
    nextLive: FIRST_LIVE,
    replacements: 0,
  };
  v.forYou = [];
  v.wallOf = {};
  if (start.initialIndicator !== NO_TILE) v.wallOf[start.initialIndicator] = FIRST_INDICATOR;
  v.seats = Array.from({ length: SEATS }, (_, s) => {
    const seat = emptySeat(start.points[s] ?? 0);
    const dealt = start.haipai[s]?.ids ?? [];
    const walls = dealWalls((s - start.dealer + SEATS) % SEATS);
    // Another seat's dealt tiles come blanked as canon's 255, not the protobuf blank: any non-tile is hidden.
    seat.known = s === v.seat || (dealt.length > 0 && dealt.every(isTile));
    if (seat.known) {
      seat.concealed = sortTiles(dealt.filter(isTile));
      dealt.forEach((t, i) => {
        if (isTile(t)) v.wallOf[t] = walls[i] ?? -1;
      });
    } else {
      seat.hidden = dealt.length;
      seat.slotWalls = walls.slice(0, dealt.length); // the hand in dealing order
    }
    return seat;
  });
  v.lastSeq = -1;
  v.lastDiscard = null;
  v.aids = null; // the hand's own come with it
  v.callout = null;
  v.result = null;
  v.prompt = null;
  v.phase = "playing";
}

function removeTile(list: number[], tile: number): boolean {
  const i = list.indexOf(tile);
  if (i < 0) return false;
  list.splice(i, 1);
  return true;
}

/** A known hand after `tile` leaves it: a drawn tile kept goes in among the others, sorted. */
function afterDiscard(concealed: readonly number[], drawn: number, tile: number): number[] {
  if (drawn === tile) return [...concealed];
  const rest = concealed.filter((t) => t !== tile);
  return drawn !== NO_TILE ? sortTiles([...rest, drawn]) : rest;
}

/** Takes `tile` out of the own seat's hand, the drawn tile first. */
function takeOwn(seat: SeatView, tile: number): void {
  seat.concealed = afterDiscard(seat.concealed, seat.drawn, tile);
  seat.drawn = NO_TILE;
}

function settleDrawn(seat: SeatView): void {
  if (seat.drawn !== NO_TILE) {
    seat.concealed = sortTiles([...seat.concealed, seat.drawn]);
    seat.drawn = NO_TILE;
  }
}

/** A hidden seat's drawn tile joins its hand at the right (a call or kan, or a draw after a kan). */
function settleHidden(seat: SeatView): void {
  if (seat.drawnWall >= 0) seat.slotWalls.push(seat.drawnWall);
  seat.drawnWall = -1;
}

/** A hidden seat's discard: the wall index of the tile that left, from the slots it reported (design 8 §2:
 *  `from` counts the drawn tile apart at the right), the kept drawn tile then going into slot `to`. Without
 *  slots, a tsumogiri is the drawn tile and anything else the rightmost slot. */
function leaveHidden(seat: SeatView, slots: { from: number; keptDraw: boolean; to: number } | undefined, tsumogiri: boolean): number {
  const n = seat.slotWalls.length;
  const from = slots ? slots.from : tsumogiri && seat.drawnWall >= 0 ? n : n - 1;
  const drawn = seat.drawnWall;
  seat.drawnWall = -1;
  if (from >= n && drawn >= 0) return drawn;
  const gone = seat.slotWalls.splice(Math.max(0, Math.min(from, n - 1)), 1)[0] ?? -1;
  if (drawn >= 0) {
    const to = slots?.keptDraw ? slots.to : seat.slotWalls.length;
    seat.slotWalls.splice(Math.max(0, Math.min(to, seat.slotWalls.length)), 0, drawn);
  }
  return gone;
}

/** Tiles a hidden seat shows from its hand for a call or kan, from the slots the call reported (design 10
 *  T11; the drawn tile apart is the last), else the rightmost; their faces are known from then on, and a
 *  drawn tile not used joins the hand at the right. */
function takeHidden(v: View, seat: SeatView, tiles: readonly number[], slots: readonly number[] = []): void {
  const shown = seat.drawnWall >= 0 ? [...seat.slotWalls, seat.drawnWall] : [...seat.slotWalls];
  seat.drawnWall = -1;
  const fits = slots.length === tiles.length && new Set(slots).size === slots.length && slots.every((i) => i < shown.length);
  const at = fits ? [...slots].sort((a, b) => a - b) : tiles.map((_, i) => shown.length - tiles.length + i).filter((i) => i >= 0);
  const taken = at.map((i) => shown[i]!);
  seat.slotWalls = shown.filter((_, i) => !at.includes(i));
  tiles.forEach((t, i) => {
    const wall = taken[i];
    if (wall !== undefined && isTile(t)) v.wallOf[t] = wall;
  });
}

function event(v: View, e: Event): void {
  if (e.seq <= v.lastSeq) return; // each event exactly once (a Resync may repeat what was applied)
  v.lastSeq = e.seq;
  const seat = v.seats[e.seat];
  const own = seat?.known ?? false;
  const b = e.body;
  switch (b.case) {
    case "draw":
    case "replacement": {
      if (!seat) return;
      let wall = -1;
      if (v.hand) {
        v.hand.wallLeft = Math.max(0, v.hand.wallLeft - 1);
        if (b.case === "draw") wall = v.hand.nextLive++;
        else {
          wall = b.value.wallIndex || FIRST_REPLACEMENT + v.hand.replacements;
          v.hand.replacements++;
        }
      }
      if (own) {
        settleDrawn(seat);
        seat.drawn = b.value.tile;
        if (isTile(b.value.tile)) v.wallOf[b.value.tile] = wall;
      } else {
        settleHidden(seat);
        seat.hidden += 1;
        seat.apart = true;
        seat.drawnWall = wall;
      }
      seat.slots = null;
      return;
    }
    case "discard": {
      if (!seat) return;
      const d = b.value;
      if (own) takeOwn(seat, d.tile);
      else {
        seat.hidden -= 1;
        const wall = leaveHidden(seat, d.slots, d.tsumogiri);
        if (wall >= 0) v.wallOf[d.tile] = wall;
      }
      seat.apart = false;
      seat.slots = d.slots ? { from: d.slots.from, keptDraw: d.slots.keptDraw, to: d.slots.to, seq: e.seq } : null;
      seat.river.push({ tile: d.tile, tsumogiri: d.tsumogiri, riichi: d.riichiDeclaration, calledAway: false, ...(d.byServer ? { byServer: true } : {}) });
      if (own && d.byServer) v.forYou.push(d.tile);
      if (d.riichiDeclaration) v.callout = { seat: e.seat, action: "DECLARE_RIICHI", seq: e.seq };
      v.lastDiscard = { seat: e.seat, index: seat.river.length - 1 };
      if (v.clock?.seat === e.seat) v.clock = null; // its turn is over; a window runs no public clock
      return;
    }
    case "call": {
      if (!seat) return;
      const c = b.value;
      const consumed = c.tiles.filter((t) => t !== c.calledTile);
      if (own) {
        settleDrawn(seat);
        for (const t of consumed) removeTile(seat.concealed, t);
      } else {
        seat.hidden -= consumed.length;
        takeHidden(v, seat, consumed, c.slots);
      }
      seat.apart = false;
      seat.slots = null;
      seat.melds.push({ type: c.type, tiles: [...c.tiles], called: c.calledTile, from: c.fromSeat });
      v.callout = { seat: e.seat, action: ActionType[c.type] ?? "", seq: e.seq };
      const river = v.seats[c.fromSeat]?.river;
      const last = river?.[river.length - 1];
      if (last && last.tile === c.calledTile) last.calledAway = true;
      v.lastDiscard = null;
      return;
    }
    case "kan": {
      if (!seat) return;
      const k = b.value;
      v.callout = { seat: e.seat, action: ActionType[k.type] ?? "", seq: e.seq };
      seat.apart = false;
      seat.slots = null;
      if (k.type === ActionType.CLOSED_KAN) {
        if (own) {
          for (const t of k.tiles) takeOwn(seat, t);
          settleDrawn(seat);
        } else {
          seat.hidden -= 4;
          takeHidden(v, seat, k.tiles, k.slots);
        }
        seat.melds.push({ type: k.type, tiles: [...k.tiles], called: NO_TILE, from: e.seat });
      } else {
        const pon = seat.melds.find((m) => m.type === ActionType.PON && k.tiles.includes(m.tiles[0] ?? NO_TILE));
        const added = k.tiles.find((t) => !pon?.tiles.includes(t));
        if (added !== undefined) {
          if (own) {
            takeOwn(seat, added);
            settleDrawn(seat);
          } else {
            seat.hidden -= 1;
            takeHidden(v, seat, [added], k.slots);
          }
        }
        if (pon) {
          pon.type = ActionType.ADDED_KAN;
          pon.tiles = [...k.tiles];
        }
      }
      return;
    }
    case "indicator":
      v.hand?.indicators.push(b.value.tile);
      v.wallOf[b.value.tile] = b.value.index;
      return;
    case "riichiAccepted":
      if (seat) seat.riichi = true;
      return;
    case "payment": {
      const p = b.value;
      if (p.reason & PENALTY_REASON) {
        // A press with nothing behind it (design 14 §6.6): the kind in the high byte, the action pressed in the low.
        const kind = (p.reason >> 8) & 0x7f;
        if (kind === PENALTY_DEAD_HAND && seat) seat.dead = true;
        v.penalties.push({ seat: e.seat, kind });
        v.callout = { seat: e.seat, action: PENALTY_CALLOUTS[kind] ?? "", seq: e.seq };
        return;
      }
      p.deltas.forEach((d, s) => {
        const target = v.seats[s];
        if (target) target.points += d;
      });
      if (v.hand && p.reason === 1) v.hand.sticks += p.deltas.filter((d) => d < 0).length; // a riichi deposit
      if (p.reason === 2) ensureResult(v).nagashi = p.deltas.flatMap((d, s) => (d > 0 ? [s] : [])); // what pays instead of noten
      return;
    }
    case "win": {
      const w = b.value;
      ensureResult(v).wins.push(w);
      // The winner lays the hand down face up; the ura indicators are turned over on the dead wall, each
      // beneath its indicator (design 2 §3.4: 127, 129, ...).
      const winner = v.seats[w.winner];
      if (winner) {
        winner.winning = w.winningTile;
        if (!winner.known) winner.shown = sortTiles((w.concealed?.ids ?? []).filter(isTile));
      }
      w.hiddenIndicators.forEach((t, k) => {
        if (!isTile(t) || !v.hand || v.hand.ura.includes(t)) return;
        v.hand.ura.push(t);
        v.wallOf[t] = FIRST_INDICATOR + 1 + 2 * k;
      });
      v.callout = { seat: w.winner, action: w.winner === w.fromSeat ? "TSUMO" : "RON", seq: e.seq };
      return;
    }
    case "exhaustive":
      ensureResult(v).exhaustive = b.value;
      // The hands that are ready are laid down face up.
      b.value.revealed.forEach((tiles, s) => {
        const seat = v.seats[s];
        if (seat && !seat.known && b.value.ready[s] && tiles.ids.length) seat.shown = sortTiles(tiles.ids.filter(isTile));
      });
      return;
    case "abortive":
      ensureResult(v).abortive = b.value;
      return;
    default:
      return; // window shells, declarations, bonus tiles and extra draws change nothing on this table
  }
}

function ensureResult(v: View): HandResult {
  if (!v.result) {
    v.result = { end: null, wins: [], pointsBefore: v.seats.map((s) => s.points), honba: v.hand?.honba ?? 0, sticks: v.hand?.sticks ?? 0 };
  }
  return v.result;
}

function resolvesPrompt(v: View, seq: number): boolean {
  return v.prompt !== null && seq > v.prompt.prompt.afterSeq;
}

export function apply(prev: View, m: ServerMessage, now: number): View {
  const v = clone(prev);
  const b = m.body;
  switch (b.case) {
    case "welcome":
      if (!b.value.seated && !v.gameId) v.phase = "idle";
      return v;
    case "tableAssigned":
      assigned(v, b.value);
      v.phase = "waiting";
      return v;
    case "commitment":
      v.phase = "shuffling";
      return v;
    case "gameStart":
      v.phase = "playing";
      return v;
    case "handStart":
      if (b.value.start) handStart(v, b.value.start, v.hand ? v.hand.index + 1 : 0);
      return v;
    case "events":
      if (v.hand) v.hand.index = b.value.handIndex;
      for (const e of b.value.events) {
        event(v, e);
        // A penalty answers nothing: the server keeps what it asked, or asks again. Taking this for an answer left a seat with no way to
        // discard when its own draw's prompt came just before the penalty, frozen until its clock ran out (review 2026-11-01).
        const penalty = e.body.case === "payment" && (e.body.value.reason & PENALTY_REASON) !== 0;
        if (!penalty && resolvesPrompt(v, e.seq)) v.prompt = null;
      }
      return v;
    case "prompt":
      v.prompt = { prompt: b.value, receivedAt: now, sent: false, rejected: false };
      return v;
    case "ack": {
      const p = v.prompt;
      if (!p || p.prompt.promptId !== b.value.promptId) return v;
      if (b.value.status === AckStatus.ILLEGAL) v.prompt = { ...p, sent: false, rejected: true };
      else if (b.value.status === AckStatus.STALE_PROMPT) v.prompt = null;
      return v;
    }
    case "clock":
      v.clock = { seat: b.value.seat, baseMsLeft: b.value.baseMsLeft, bankMsLeft: b.value.bankMsLeft, receivedAt: now };
      return v;
    case "connection": {
      const c = v.connections[b.value.seat];
      if (c) {
        c.connected = b.value.connected;
        c.auto = b.value.auto;
      }
      return v;
    }
    case "handEnd": {
      const end = b.value.end;
      if (!end) return v;
      const r = ensureResult(v);
      r.end = end;
      end.points.forEach((p, s) => {
        const seat = v.seats[s];
        if (seat) seat.points = p;
      });
      if (v.hand) {
        v.hand.sticks = end.sticks; // both the next hand's, so they never disagree between hands
        v.hand.honba = end.repeatCount;
      }
      v.prompt = null;
      v.clock = null;
      return v;
    }
    case "gameEnd":
      v.gameEnd = b.value.end ?? null;
      v.phase = "ended";
      v.prompt = null;
      v.clock = null;
      return v;
    case "resync":
      return resync(v, b.value, now);
    case "aids":
      v.aids = { waits: [...b.value.waits], furiten: b.value.furiten, noYaku: [...b.value.noYaku], noRon: [...b.value.noRon] };
      return v;
    case "error":
      v.error = { code: b.value.code, detail: b.value.detail };
      if (b.value.code === ErrorCode.TABLE_DISSOLVED) {
        v.phase = "idle"; // no game: the start failed twice, and the table is open again (design 4 §7)
        v.prompt = null;
      }
      // Nothing more will come of this game: nothing to answer, and no clock to run out.
      if (b.value.code === ErrorCode.GAME_SERVER_DOWN || b.value.code === ErrorCode.INTERNAL) {
        v.prompt = null;
        v.clock = null;
      }
      return v;
    default:
      return v; // welcome, nonce receipts and pongs are the connection's business
  }
}

function resync(prev: View, r: Extract<ServerMessage["body"], { case: "resync" }>["value"], now: number): View {
  const v = emptyView();
  if (r.table) assigned(v, r.table);
  const start = r.handStart?.start;
  if (start) {
    handStart(v, start, r.events?.handIndex ?? r.prompt?.handIndex ?? prev.hand?.index ?? 0);
    for (const e of r.events?.events ?? []) event(v, e);
  }
  // Between hands: the hand that just ended, with its result (the server sends it only then).
  const ended = r.previousHandEnd?.end;
  if (ended) {
    ensureResult(v).end = ended;
    ended.points.forEach((p, s) => {
      const seat = v.seats[s];
      if (seat) seat.points = p;
    });
    if (v.hand) {
      v.hand.sticks = ended.sticks;
      v.hand.honba = ended.repeatCount;
    }
  }
  // Points come from the hand's start and events, exactly as live; the Resync's own list is only for a
  // Resync without a hand (it is never ahead of the stream, but a second source could only disagree).
  if (!start && r.points.length === SEATS) r.points.forEach((p, s) => (v.seats[s]!.points = p));
  r.connections.forEach((c) => {
    const target = v.connections[c.seat];
    if (target) {
      target.connected = c.connected;
      target.auto = c.auto;
    }
  });
  if (r.clock) v.clock = { seat: r.clock.seat, baseMsLeft: r.clock.baseMsLeft, bankMsLeft: r.clock.bankMsLeft, receivedAt: now };
  if (r.prompt) v.prompt = { prompt: r.prompt, receivedAt: now, sent: false, rejected: false };
  // What went while this tab was away: a Resync replays the whole hand, and the river as last seen says which of it is news.
  const seen = prev.hand?.index === v.hand?.index ? (prev.seats[prev.seat]?.river.length ?? 0) : 0;
  v.forYou = [...(prev.hand?.index === v.hand?.index ? prev.forYou : []), ...(v.seats[v.seat]?.river ?? []).slice(seen).filter((x) => x.byServer).map((x) => x.tile)];
  if (r.aids) v.aids = { waits: [...r.aids.waits], furiten: r.aids.furiten, noYaku: [...r.aids.noYaku], noRon: [...r.aids.noRon] };
  if (start) v.phase = "playing";
  return v;
}

/** Marks prompt `promptId` as answered, so the controls lock until the server replies; a newer prompt that
 * arrived meanwhile stays open. */
export function markSent(prev: View, promptId: number): View {
  const p = prev.prompt;
  return p && p.prompt.promptId === promptId ? { ...prev, forYou: [], prompt: { ...p, sent: true, rejected: false } } : prev;
}

/** The view with the player's own discard already made: what they pressed moves as they press it, not a round trip later (a phone on
 *  150 ms saw the tile drop back and wait 280 ms, 2026-11-01). Only ever a picture over the server's view, which stays as it was: once
 *  `promptId` is answered, refused or asked again this gives that view back, and a tile the server would not take goes home. */
export function withOwnDiscard(v: View, promptId: number, tile: number): View {
  const p = v.prompt;
  const seat = v.seats[v.seat];
  if (!p || p.prompt.promptId !== promptId || !p.sent || p.rejected || !seat?.known) return v;
  const out = clone(v);
  const s = out.seats[out.seat]!;
  const tsumogiri = s.drawn === tile;
  takeOwn(s, tile);
  s.river.push({ tile, tsumogiri, riichi: false, calledAway: false });
  out.lastDiscard = { seat: out.seat, index: s.river.length - 1 };
  if (out.clock?.seat === out.seat) out.clock = null;
  return out;
}

/** The hand is in its first go-around, so a table first drawn now plays the deal. Not only before the first event: the
 *  first draw can come in the same frame as the hand, and a tab can be a moment late to its table, its code and pictures
 *  still loading; the deal then went missing at random, on a player's first game above all (owner, 2026-10-31). */
export function dealing(v: View): boolean {
  return v.hand !== null && v.hand.wallLeft >= LIVE_WALL - SEATS;
}

export function handEnded(v: View): boolean {
  return v.result?.end != null && v.result.end.outcome !== HandEnd_Outcome.OUTCOME_UNSPECIFIED;
}

/** Copies of `kind` this seat cannot see: four less its own tiles, every river, meld and dora indicator. */
export function unseen(v: View, kind: number): number {
  let seen = 0;
  const count = (ids: readonly number[]) => ids.forEach((id) => (seen += id !== NO_TILE && kindOf(id) === kind ? 1 : 0));
  const me = v.seats[v.seat];
  if (me) count([...me.concealed, me.drawn]);
  for (const s of v.seats) {
    count(s.river.filter((r) => !r.calledAway).map((r) => r.tile)); // a called tile is counted in its meld
    for (const m of s.melds) count(m.tiles);
  }
  count(v.hand?.indicators ?? []);
  return Math.max(0, 4 - seen);
}
